import { Folder, GlobalAction, GlobalState, LibraryItem } from '.';
import { mergeDeep } from '../utils/object';
import { VideoListItem } from '../api';

const GET_VIDEOS_START = 'GET_VIDEOS_START';
const GET_VIDEOS_SUCCESS = 'GET_VIDEOS_SUCCESS';
const GET_VIDEOS_ERROR = 'GET_VIDEOS_ERROR';
const GET_FOLDERS_START = 'GET_FOLDERS_START';
const GET_FOLDERS_SUCCESS = 'GET_FOLDERS_SUCCESS';
const GET_FOLDERS_ERROR = 'GET_FOLDERS_ERROR';
const INIT_LIBRARY_START = 'INIT_LIBRARY_START';
const INIT_LIBRARY_SUCCESS = 'INIT_LIBRARY_SUCCESS';
const INIT_LIBRARY_ERROR = 'INIT_LIBRARY_ERROR';
const INIT_LIBRARY_REFRESH = 'INIT_LIBRARY_REFRESH';
const SET_LIBRARY_FOLDER = 'SET_LIBRARY_FOLDER';
const DELETE_VIDEOS_START = 'DELETE_VIDEOS_START';
const DELETE_VIDEOS_SUCCESS = 'DELETE_VIDEOS_SUCCESS';
const DELETE_VIDEOS_ERROR = 'DELETE_VIDEOS_ERROR';
const UPDATE_VIDEO_START = 'UPDATE_VIDEO_START';
const UPDATE_VIDEOS_SUCCESS = 'UPDATE_VIDEOS_SUCCESS';
const UPDATE_VIDEO_ERROR = 'UPDATE_VIDEO_ERROR';
const MOVE_VIDEO_SUCCESS = 'MOVE_VIDEO_SUCCESS';
const CLEAR_ERROR = 'CLEAR_ERROR';
const UPDATE_VIDEO_TAGS_START = 'UPDATE_VIDEO_TAGS_START';
const UPDATE_VIDEO_TAGS_ERROR = 'UPDATE_VIDEO_TAGS_ERROR';
const UPDATE_VIDEO_TAGS_SUCCESS = 'UPDATE_VIDEO_TAGS_SUCCESS';
const GET_VIDEO_TAGS_START = 'GET_VIDEO_TAGS_START';
const GET_VIDEO_TAGS_ERROR = 'GET_VIDEO_TAGS_ERROR';
const GET_VIDEO_TAGS_SUCCESS = 'GET_VIDEO_TAGS_SUCCESS';
const DUPLICATE_VIDEOS_START = 'DUPLICATE_VIDEOS_START';
const DUPLICATE_VIDEOS_SUCCESS = 'DUPLICATE_VIDEOS_SUCCESS';
const DUPLICATE_VIDEOS_ERROR = 'DUPLICATE_VIDEOS_ERROR';
const CREATE_FOLDER_START = 'CREATE_FOLDER_START';
const CREATE_FOLDER_SUCCESS = 'CREATE_FOLDER_SUCCESS';
const CREATE_FOLDER_ERROR = 'CREATE_FOLDER_ERROR';
const DELETE_FOLDER_START = 'DELETE_FOLDER_START';
const DELETE_FOLDER_SUCCESS = 'DELETE_FOLDER_SUCCESS';
const DELETE_FOLDER_ERROR = 'DELETE_FOLDER_ERROR';
const UPDATE_FOLDER_START = 'UPDATE_FOLDER_START';
const UPDATE_FOLDER_SUCCESS = 'UPDATE_FOLDER_SUCCESS';
const UPDATE_FOLDER_ERROR = 'UPDATE_FOLDER_ERROR';
const INCREMENT_CONVERSATION_NOTIFICATION =
  'INCREMENT_CONVERSATION_NOTIFICATION';
const DECREMENT_CONVERSATION_NOTIFICATION =
  'DECREMENT_CONVERSATION_NOTIFICATION';
const SET_UNREAD_MESSAGES = 'SET_UNREAD_MESSAGES';
const UPDATE_VIDEO_LIMIT_STATUS = 'UPDATE_VIDEO_LIMIT_STATUS';

const filterDeletedVideos = (videos: VideoListItem[], ids: any[]) => {
  return videos.filter(
    (v: VideoListItem) => !ids.includes(v.id) && !ids.includes(parseInt(v.id))
  );
};

export const globalReducer = (
  state: GlobalState,
  action: GlobalAction
): GlobalState => {
  const { payload, type } = action;
  switch (type) {
    case GET_VIDEOS_START: {
      const newState = {
        ...state,
      };

      // remove cached videos to not show preview of old videos when searching
      Object.keys(newState.library).forEach(folder => {
        if (newState.library[folder]) {
          newState.library[folder].videos = [];
          newState.library[folder].count = 0;
        }
      });

      return { ...newState };
    }

    case GET_VIDEOS_SUCCESS: {
      const { folder, videos, count, page, size, search } = payload;
      const libraryItem = { videos, count, page, size, search };
      const library = { ...state.library, [folder]: libraryItem };
      const newState = { ...state, library };
      return newState;
    }

    case INIT_LIBRARY_SUCCESS: {
      const { folders, currentFolder } = payload;
      const library = folders.reduce((item: LibraryItem, folder: Folder) => {
        if (!item[folder.name]) {
          item[folder.name] = { videos: [], search: '' };
        }
        return item;
      }, state.library);
      return {
        ...state,
        folders,
        library: mergeDeep(state.library, library) as LibraryItem,
        currentFolder,
      };
    }

    case SET_LIBRARY_FOLDER: {
      return { ...state, currentFolder: payload };
    }

    case CREATE_FOLDER_SUCCESS: {
      const libraryItem = {
        videos: [],
        count: 0,
        page: 0,
        size: 10,
        search: '',
      };

      return {
        ...state,
        currentFolder: payload,
        folders: state.folders ? [...state.folders, payload] : [payload],
        library: { ...state.library, [payload.name]: libraryItem },
      };
    }

    case UPDATE_FOLDER_SUCCESS: {
      const libraryItem = state.library[payload.oldName];
      const folders = state.folders ? [...state.folders] : [];
      const folder = folders.find(f => f.folderId === payload.folderId);
      if (folder) {
        folder.name = payload.newName;
      }

      return {
        ...state,
        currentFolder: folder,
        folders,
        library: { ...state.library, [payload.newName]: libraryItem },
      };
    }

    case DELETE_FOLDER_SUCCESS: {
      return {
        ...state,
        folders: state.folders?.filter(
          f => f.folderId !== payload.folder.folderId
        ),
        currentFolder: state.folders && state.folders[0],
      };
    }

    case UPDATE_VIDEO_TAGS_SUCCESS: {
      const videos = [
        ...state.library[state.currentFolder?.name || 'Primary'].videos,
      ];
      const video = videos.find(v => v.id.toString() === payload.id);
      if (video) {
        video.tags = payload.tags;
      }

      return {
        ...state,
        library: {
          ...state.library,
          [state.currentFolder?.name || 'Primary']: {
            ...state.library[state.currentFolder?.name || 'Primary'],
            videos,
          },
        },
      };
    }

    case UPDATE_VIDEOS_SUCCESS: {
      return {
        ...state,
        library: {
          ...state.library,
          [payload.folder]: {
            ...state.library[payload.folder],
            videos: payload.updated,
          },
        },
      };
    }

    case MOVE_VIDEO_SUCCESS: {
      if (payload.isCopyVideoSelected) {
        return state;
      }
      return {
        ...state,
        library: {
          ...state.library,
          [state.currentFolder?.name || 'Primary']: {
            ...state.library[payload.currentFolder],
            videos: filterDeletedVideos(
              state.library[state.currentFolder?.name || 'Primary'].videos,
              payload.ids
            ),
          },
        },
      };
    }

    case DELETE_VIDEOS_SUCCESS: {
      const { ids } = payload;
      const { library: oldLibrary, currentFolder } = state;
      const oldLibraryItem = oldLibrary[currentFolder?.name || 'Primary'];
      const { videos } = oldLibraryItem;
      const newVideos = [...filterDeletedVideos(videos, ids)];
      const newLibraryItem = { ...oldLibraryItem, videos: newVideos };
      const library = {
        ...state.library,
        [currentFolder?.name || 'Primary']: newLibraryItem,
      };
      return { ...state, library };
    }
    case SET_UNREAD_MESSAGES: {
      return { ...state, unreadMessagesCount: payload };
    }

    case INCREMENT_CONVERSATION_NOTIFICATION: {
      return {
        ...state,
        unreadMessagesCount: Number(state?.unreadMessagesCount) + 1,
      };
    }
    case DECREMENT_CONVERSATION_NOTIFICATION: {
      return {
        ...state,
        unreadMessagesCount:
          Number(state?.unreadMessagesCount) === 0
            ? 0
            : Number(state?.unreadMessagesCount) - 1,
      };
    }
    case UPDATE_VIDEO_LIMIT_STATUS: {
      const { videoLimitStatus } = payload;
      return {
        ...state,
        videoLimitStatus: videoLimitStatus,
      };
    }
    default:
      return state;
  }
};

export const GLOBAL_ACTIONS = {
  GET_VIDEOS_START,
  GET_VIDEOS_SUCCESS,
  GET_VIDEOS_ERROR,
  GET_FOLDERS_START,
  GET_FOLDERS_SUCCESS,
  GET_FOLDERS_ERROR,
  INIT_LIBRARY_START,
  INIT_LIBRARY_SUCCESS,
  INIT_LIBRARY_ERROR,
  INIT_LIBRARY_REFRESH,
  SET_LIBRARY_FOLDER,
  MOVE_VIDEO_SUCCESS,
  DELETE_VIDEOS_START,
  DELETE_VIDEOS_SUCCESS,
  DELETE_VIDEOS_ERROR,
  UPDATE_VIDEO_START,
  UPDATE_VIDEOS_SUCCESS,
  UPDATE_VIDEO_ERROR,
  CLEAR_ERROR,
  UPDATE_VIDEO_TAGS_START,
  UPDATE_VIDEO_TAGS_ERROR,
  UPDATE_VIDEO_TAGS_SUCCESS,
  GET_VIDEO_TAGS_START,
  GET_VIDEO_TAGS_ERROR,
  GET_VIDEO_TAGS_SUCCESS,
  DUPLICATE_VIDEOS_START,
  DUPLICATE_VIDEOS_SUCCESS,
  DUPLICATE_VIDEOS_ERROR,
  CREATE_FOLDER_START,
  CREATE_FOLDER_ERROR,
  CREATE_FOLDER_SUCCESS,
  DELETE_FOLDER_START,
  DELETE_FOLDER_ERROR,
  DELETE_FOLDER_SUCCESS,
  UPDATE_FOLDER_START,
  UPDATE_FOLDER_ERROR,
  UPDATE_FOLDER_SUCCESS,
  INCREMENT_CONVERSATION_NOTIFICATION,
  DECREMENT_CONVERSATION_NOTIFICATION,
  SET_UNREAD_MESSAGES,
  UPDATE_VIDEO_LIMIT_STATUS,
};
